"use client"

import { useEffect } from "react"
import {
  trackBeginCheckout,
  trackPurchase,
  trackViewItem,
  type EcommercePayload,
  type PurchasePayload,
} from "@/lib/analytics/track"

// ---------------------------------------------------------------------------
// TrackEcommerceEvent: lets a SERVER page raise a page-level ecommerce event
// (view_item on the PDP, begin_checkout on checkout, purchase on the
// confirmation page). The page builds the payload with the mappers in
// src/lib/analytics/ecommerce.ts and passes it as plain data.
//
// Renders nothing, so it is hydration-safe. The effect is keyed on the
// serialized payload, so a server re-render with identical data (e.g.
// router.refresh()) does not raise the event again. Consent and the
// once-per-order rule for `purchase` are enforced in track.ts.
// ---------------------------------------------------------------------------

type Props =
  | { event: "view_item"; payload: EcommercePayload }
  | {
      event: "begin_checkout"
      payload: EcommercePayload
      /** Raise at most once per key per browser tab session (e.g. the cart id). */
      onceKey?: string
    }
  | { event: "purchase"; payload: PurchasePayload }

const BEGIN_CHECKOUT_KEY_PREFIX = "appslu:begin-checkout:"

function firstTimeThisSession(key: string): boolean {
  try {
    const storageKey = BEGIN_CHECKOUT_KEY_PREFIX + key
    if (window.sessionStorage.getItem(storageKey)) return false
    window.sessionStorage.setItem(storageKey, "1")
  } catch {
    // Storage blocked: fall through and raise the event (over-counting a
    // checkout start is harmless; it is not revenue).
  }
  return true
}

export function TrackEcommerceEvent(props: Props) {
  const { event } = props
  const serialized = JSON.stringify(props.payload)
  const onceKey = props.event === "begin_checkout" ? props.onceKey : undefined

  useEffect(() => {
    const payload = JSON.parse(serialized) as EcommercePayload & PurchasePayload
    if (event === "view_item") trackViewItem(payload)
    else if (event === "purchase") trackPurchase(payload)
    else if (!onceKey || firstTimeThisSession(onceKey)) trackBeginCheckout(payload)
  }, [event, serialized, onceKey])

  return null
}
